import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Header from "../../components/home/Header";
import Nav from "../../components/home/Nav";
import { useFilterProductsQuery } from "../../store/services/homeProducts";
import ProductCard from "../../components/home/ProductCard";
import Pagination from "../../components/Pagination";
import ProductSkeleton from "../../components/home/ProductSkeleton";
import ProductFilters from "../../components/home/ProductFilters";
import MobileAppendProducts from "../../components/home/MobileAppendProducts";
import { toPersianNumber } from "../../utils/persian";
import { categoryDisplayName, categoryUrl } from "../../utils/categoryUrl";

const categoryBannerImages = {
  "کفش مجلسی": "/storage/categories/banners/kafshmajlesi.webp",
  "اسپورت": "/storage/categories/banners/kafshsport.webp",
  "صندل زنانه": "/storage/categories/banners/sandal.webp",
  "کفش پرسنلی": "/storage/categories/banners/kafshpersnelo.webp",
};

const getInitialFilters = (search, name, page) => {
  const params = new URLSearchParams(search);

  return {
    q: "",
    category: name,
    fixedCategory: name,
    brand: params.get("brand") || "",
    color: params.get("color") || "",
    size: params.get("size") || "",
    min_price: params.get("min_price") || "",
    max_price: params.get("max_price") || "",
    available: params.get("available") || "",
    sort: params.get("sort") || "latest",
    page: parseInt(page)
  };
};

const CatProducts = () => {
  const { name, page = 1 } = useParams();
  const categoryTitle = categoryDisplayName(name);
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [filters, setFilters] = useState(() => getInitialFilters(location.search, name, page));

  useEffect(() => {
    const params = new URLSearchParams();

    ["brand", "color", "size", "min_price", "max_price", "available", "sort"].forEach((key) => {
      if (filters[key] && !(key === "sort" && filters[key] === "latest")) {
        params.set(key, filters[key]);
      }
    });

    const query = params.toString();
    const nextPage = parseInt(page) || 1;
    const url = `${categoryUrl(name, nextPage)}${query ? `?${query}` : ""}`;

    if (url !== `${location.pathname}${location.search}`) {
      navigate(url, { replace: true });
    }
  }, [filters, name, page, navigate, location.pathname, location.search]);

  const queryParams = useMemo(
    () => ({
      category: name,
      brand: filters.brand,
      color: filters.color,
      size: filters.size,
      min_price: filters.min_price,
      max_price: filters.max_price,
      available: filters.available,
      sort: filters.sort,
      page: parseInt(page)
    }),
    [name, filters, page]
  );

  const { data, isFetching } = useFilterProductsQuery(queryParams);

  return (
    <>
      <Nav />

      <div className="mt-[76px]">
        <Header image={categoryBannerImages[categoryTitle]}>{categoryTitle}</Header>
      </div>

      <section className="shop-page">
        <div className="site-container">
          <div className="shop-list-topbar">
            <div className="shop-result-meta">
              <span>{toPersianNumber(data?.count || 0)} محصول</span>
              <strong>{categoryTitle}</strong>
              <p>با فیلترها سریع‌تر محصول مناسب خودت را پیدا کن.</p>
            </div>

            <select
              aria-label="مرتب‌سازی محصولات"
              value={filters.sort}
              onChange={(e) => setFilters((prev) => ({ ...prev, sort: e.target.value, page: 1 }))}
            >
              <option value="latest">جدیدترین</option>
              <option value="cheapest">ارزان‌ترین</option>
              <option value="expensive">گران‌ترین</option>
              <option value="popular">محبوب‌ترین</option>
              <option value="discount">بیشترین تخفیف</option>
            </select>
          </div>

          <div className="product-list-layout">
            <ProductFilters
              filters={filters}
              setFilters={setFilters}
              filterOptions={data?.filters || {}}
              count={data?.count || 0}
              open={mobileFilterOpen}
              setOpen={setMobileFilterOpen}
            />

            <section className="product-list-content">
              {isFetching ? (
                <ProductSkeleton />
              ) : data?.count > 0 ? (
                <>
                  <div className="product-grid-desktop">
                    <div className="product-grid">
                      {data.products.map((product, index) => (
                        <ProductCard product={product} key={product._id || product.id} index={index} priority={index < 4} />
                      ))}
                    </div>

                    <Pagination page={parseInt(page)} perPage={data.perPage} count={data.count} path={`category/${name}`} />
                  </div>

                  <MobileAppendProducts
                    initialProducts={data.products}
                    count={data.count}
                    perPage={data.perPage}
                    queryParams={queryParams}
                    resetKey={`${name}-${JSON.stringify(filters)}`}
                  />
                </>
              ) : (
                <div className="shop-empty-state">
                  <strong>محصولی پیدا نشد</strong>
                  <p>برای دسته «{categoryTitle}» محصولی با فیلترهای فعلی وجود ندارد. فیلترها را پاک کن یا مقدارها را تغییر بده.</p>
                </div>
              )}
            </section>
          </div>
        </div>
      </section>
    </>
  );
};

export default CatProducts;
